/**
 * Audit Log Filters
 *
 * Date range and entity/action filters for audit log views.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from "@wondermaps/ui";
import { DatePickerField } from "../form/DatePickerField";

interface AuditLogFiltersProps {
  entityType: string;
  setEntityType: (value: string) => void;
  action: string;
  setAction: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  source: "content" | "security" | "all";
  setSource: (value: "content" | "security" | "all") => void;
  showSourceFilter?: boolean;
}

const ENTITY_TYPES = [
  { value: "all", label: "All entities" },
  { value: "poi", label: "POI" },
  { value: "category", label: "Category" },
  { value: "tag", label: "Tag" },
  { value: "map", label: "Map" },
  { value: "layer", label: "Layer" },
  { value: "member", label: "Member" },
  { value: "invite", label: "Invite" },
  { value: "feature_flag", label: "Feature Flag" },
  { value: "org", label: "Organization" },
];

const ACTIONS = [
  { value: "all", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "publish", label: "Publish" },
  { value: "restore", label: "Restore" },
  { value: "accept", label: "Accept" },
  { value: "revoke", label: "Revoke" },
];

const SOURCES = [
  { value: "all", label: "All sources" },
  { value: "content", label: "Content only" },
  { value: "security", label: "Security only" },
];

export function AuditLogFilters({
  entityType,
  setEntityType,
  action,
  setAction,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  source,
  setSource,
  showSourceFilter = true,
}: AuditLogFiltersProps) {
  return (
    <div className="flex flex-wrap gap-4 mb-4">
      {/* Source filter */}
      {showSourceFilter && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Source</Label>
          <Select
            value={source}
            onValueChange={(v) => setSource(v as typeof source)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Entity type filter */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Entity</Label>
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((e) => (
              <SelectItem key={e.value || "all"} value={e.value}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action filter */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Action</Label>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTIONS.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date range */}
      <DatePickerField label="From" value={startDate} onChange={setStartDate} className="w-[140px]" />
      <DatePickerField label="To" value={endDate} onChange={setEndDate} className="w-[140px]" />
    </div>
  );
}

